import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useCreateManualCaixaTransaction } from "@/features/caixas/hooks";
import {
  listSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier,
  getSupplierOwnBoxBalance,
  listSupplierOwnBoxTransactions,
  getSupplierReportStatuses,
  getSupplierReportStatus,
} from "./api";
import type { ListSuppliersParams } from "./api";
import type {
  CreateSupplierPayload,
  UpdateSupplierPayload,
  SupplierReportStatusParams,
  SupplierBoxTransactionsParams,
} from "./types";
import {
  SUPPLIER_CAIXAS_KEY,
  SUPPLIER_REPORT_STATUSES_KEY,
  SUPPLIERS_QUERY_KEY,
} from "@/features/caixas/queryKeys";

export {
  SUPPLIER_CAIXAS_KEY,
  SUPPLIER_REPORT_STATUSES_KEY,
  SUPPLIERS_QUERY_KEY,
};
const QUERY_KEY = SUPPLIERS_QUERY_KEY;

export function useSuppliers(
  params: ListSuppliersParams & { enabled?: boolean } = {},
) {
  const { enabled, ...queryParams } = params;
  const limit = queryParams.limit ?? 2000;
  const offset = queryParams.offset ?? 0;
  const search = queryParams.search?.trim() ?? "";
  const lastSuccessfulPage = useRef<
    Awaited<ReturnType<typeof listSuppliers>> | undefined
  >(undefined);

  const result = useQuery({
    queryKey: [...QUERY_KEY, { limit, offset, search }],
    queryFn: ({ signal }) => listSuppliers({ limit, offset, search }, signal),
    enabled: enabled ?? true,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (result.isSuccess && !result.isPlaceholderData) {
      lastSuccessfulPage.current = result.data;
    }
  }, [result.data, result.isPlaceholderData, result.isSuccess]);

  if (result.isError && !result.data && lastSuccessfulPage.current) {
    return {
      ...result,
      data: lastSuccessfulPage.current,
      isPlaceholderData: true,
    };
  }

  return result;
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateSupplierPayload) => createSupplier(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useUpdateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateSupplierPayload;
    }) => updateSupplier(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useDeleteSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSupplier(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });
}

export function useCreateManualSupplierBoxTransaction() {
  return useCreateManualCaixaTransaction();
}

export function useSupplierOwnBoxBalance() {
  return useQuery({
    queryKey: [...SUPPLIER_CAIXAS_KEY, "balance"],
    queryFn: getSupplierOwnBoxBalance,
  });
}

export function useSupplierOwnBoxTransactions(
  params: SupplierBoxTransactionsParams,
) {
  return useQuery({
    queryKey: [...SUPPLIER_CAIXAS_KEY, "list", params],
    queryFn: ({ signal }) => listSupplierOwnBoxTransactions(params, signal),
    placeholderData: keepPreviousData,
  });
}

export function useSupplierReportStatuses(supplierIds: string[]) {
  const normalizedIds = Array.from(
    new Set(supplierIds.map((id) => id.trim()).filter(Boolean)),
  ).sort();
  return useQuery({
    queryKey: [...SUPPLIER_REPORT_STATUSES_KEY, { supplierIds: normalizedIds }],
    queryFn: ({ signal }) => getSupplierReportStatuses(normalizedIds, signal),
    enabled: normalizedIds.length > 0,
  });
}

export function useSupplierReportStatus(
  supplierId: string,
  params: SupplierReportStatusParams,
) {
  return useQuery({
    queryKey: [...SUPPLIER_REPORT_STATUSES_KEY, supplierId, params],
    queryFn: ({ signal }) =>
      getSupplierReportStatus(supplierId, params, signal),
    enabled: !!supplierId,
  });
}
